import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { getLogoWhite } from "@/utils/assets";
import { CouponsPanel } from "@/components/admin/CouponsPanel";
import { EmailComposer } from "@/components/admin/EmailComposer";
import { EmailTemplateEditor } from "@/components/admin/EmailTemplateEditor";
import { PublicationsPanel } from "@/components/admin/PublicationsPanel";

import { adminContent } from "@/i18n/admin";
import { LanguageProvider, useLang } from "@/i18n/language";
import { portalContent, type CurrencyKey, type PathwayKey } from "@/i18n/portal";
import { supabase } from "@/integrations/supabase/client";
import { formatDate, formatDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Dirección Académica STM | Share The Med" },
      {
        name: "description",
        content:
          "Panel interno de Share The Med para revisar solicitudes, habilitar pagos y organizar grupos.",
      },
      { property: "og:title", content: "Dirección Académica STM | Share The Med" },
      {
        property: "og:description",
        content: "Revisión de solicitudes, historial de comentarios y gestión de grupos STM.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <LanguageProvider>
      <AdminPage />
    </LanguageProvider>
  ),
});

const inputClass =
  "w-full rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary";
const cardClass = "rounded-xl border border-border bg-surface p-6";
const btnPrimary =
  "rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50";

const STATUSES = ["submitted", "under_review", "accepted", "rejected", "paid"] as const;
type StatusKey = (typeof STATUSES)[number];

const groupSchema = z.object({
  name: z.string().trim().min(3).max(120),
  pathway: z.enum(["basic", "intermediate", "advanced"]),
  starts_on: z.string().trim().optional(),
  capacity: z.number().int().min(1).max(200),
  notes: z.string().trim().max(500).optional(),
});

function money(amount: number, currency: CurrencyKey) {
  return new Intl.NumberFormat(currency === "MXN" ? "es-MX" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function AdminPage() {
  const logo = getLogoWhite();
  const { lang, toggle } = useLang();
  const t = adminContent[lang];
  const p = portalContent[lang].portal;
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<
    "applications" | "groups" | "templates" | "coupons" | "publications"
  >("applications");

  const [filter, setFilter] = useState<"all" | StatusKey>("all");

  const roleQuery = useQuery({
    queryKey: ["my-roles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("role");
      if (error) throw error;
      return data.map((r) => r.role);
    },
  });

  const appsQuery = useQuery({
    queryKey: ["admin-applications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const ids = [...new Set(data.map((a) => a.user_id))];
      let profiles: Record<string, { full_name: string | null; email: string | null }> = {};
      if (ids.length) {
        const { data: profileRows } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", ids);
        profiles = Object.fromEntries(
          (profileRows ?? []).map((r) => [r.id, { full_name: r.full_name, email: r.email }]),
        );
      }
      return data.map((a) => ({
        ...a,
        full_name: profiles[a.user_id]?.full_name ?? null,
        email: profiles[a.user_id]?.email ?? null,
      }));
    },
    enabled: roleQuery.data?.includes("admin") ?? false,
  });

  const eventsQuery = useQuery({
    queryKey: ["admin-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("application_events")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: roleQuery.data?.includes("admin") ?? false,
  });

  const groupsQuery = useQuery({
    queryKey: ["admin-groups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("groups")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: roleQuery.data?.includes("admin") ?? false,
  });

  const membersQuery = useQuery({
    queryKey: ["admin-group-members"],
    queryFn: async () => {
      const { data, error } = await supabase.from("group_members").select("*");
      if (error) throw error;
      return data;
    },
    enabled: roleQuery.data?.includes("admin") ?? false,
  });

  const isAdmin = roleQuery.data?.includes("admin") ?? false;

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["admin-applications"] });
    queryClient.invalidateQueries({ queryKey: ["admin-events"] });
    queryClient.invalidateQueries({ queryKey: ["admin-groups"] });
    queryClient.invalidateQueries({ queryKey: ["admin-group-members"] });
  }

  if (roleQuery.isLoading) {
    return <main className="mx-auto max-w-4xl px-5 py-16 text-sm text-muted-foreground">…</main>;
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-24 text-center">
        <p className="text-sm text-muted-foreground">{t.denied}</p>
        <Link to="/portal" className={`${btnGhost} mt-6 inline-block`}>
          {t.back}
        </Link>
      </main>
    );
  }

  const apps = (appsQuery.data ?? []).filter((a) => filter === "all" || a.status === filter);
  const groups = groupsQuery.data ?? [];
  const members = membersQuery.data ?? [];
  const memberCounts = members.reduce<Record<string, number>>((acc, m) => {
    acc[m.group_id] = (acc[m.group_id] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <main className="mx-auto max-w-6xl px-5 py-12 md:px-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo.url} alt="Share The Med" className="h-9 w-auto" />
          <span className="text-sm font-semibold tracking-[0.22em] uppercase">
            Share <span className="text-primary">the</span> Med
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggle} className={btnGhost}>
            {lang === "es" ? "EN" : "ES"}
          </button>
          <Link to="/portal" className={btnGhost}>
            {t.back}
          </Link>
        </div>
      </header>

      <div className="mt-12">
        <p className="eyebrow text-primary">{t.title}</p>
        <h1 className="mt-3 text-3xl font-semibold md:text-4xl">{t.tabs[tab]}</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t.lead}</p>
      </div>

      <div className="mt-8 flex gap-2">
        {(["applications", "groups", "coupons", "publications", "templates"] as const).map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={
              tab === key
                ? "rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
                : btnGhost
            }
          >
            {t.tabs[key]}
          </button>
        ))}
      </div>

      {tab === "applications" ? (
        <section className="mt-8">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFilter("all")}
              className={filter === "all" ? `${btnPrimary}` : btnGhost}
            >
              {t.apps.filterAll}
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={filter === s ? `${btnPrimary}` : btnGhost}
              >
                {p.statuses[s]}
              </button>
            ))}
          </div>

          {apps.length === 0 ? (
            <p className="mt-8 text-sm text-muted-foreground">{t.apps.empty}</p>
          ) : (
            <div className="mt-6 space-y-5">
              {apps.map((app) => (
                <ApplicationCard
                  key={app.id}
                  app={app}
                  events={(eventsQuery.data ?? []).filter((e) => e.application_id === app.id)}
                  groups={groups}
                  memberCounts={memberCounts}
                  member={members.find((m) => m.application_id === app.id) ?? null}
                  onChanged={refresh}
                />
              ))}
            </div>
          )}
        </section>
      ) : tab === "coupons" ? (
        <section className="mt-8">
          <CouponsPanel />
        </section>
      ) : tab === "publications" ? (
        <section className="mt-8">
          <PublicationsPanel />
        </section>
      ) : tab === "templates" ? (
        <section className="mt-8">
          <EmailTemplateEditor />
        </section>
      ) : (
        <section className="mt-8 grid gap-6 lg:grid-cols-[380px_1fr]">
          <NewGroupForm onCreated={refresh} />
          <div className="space-y-4">
            {groups.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.groups.empty}</p>
            ) : (
              groups.map((g) => (
                <GroupCard
                  key={g.id}
                  group={g}
                  members={members.filter((m) => m.group_id === g.id)}
                  apps={appsQuery.data ?? []}
                  onChanged={refresh}
                />
              ))
            )}
          </div>
        </section>
      )}
    </main>
  );
}

type AppRow = {
  id: string;
  user_id: string;
  pathway: string;
  currency: string;
  country: string;
  medical_area: string;
  notes: string | null;
  amount: number | null;
  status: string;
  reviewer_notes: string | null;
  created_at: string;
  full_name?: string | null;
  email?: string | null;
};

type EventRow = {
  id: string;
  application_id: string;
  from_status: string | null;
  to_status: string | null;
  comment: string | null;
  created_at: string;
};

type GroupRow = {
  id: string;
  name: string;
  pathway: string;
  starts_on: string | null;
  capacity: number;
  notes: string | null;
  is_active: boolean;
};

type MemberRow = {
  id: string;
  group_id: string;
  application_id: string;
  user_id: string;
};

function ApplicationCard({
  app,
  events,
  groups,
  memberCounts,
  member,
  onChanged,
}: {
  app: AppRow;
  events: EventRow[];
  groups: GroupRow[];
  memberCounts: Record<string, number>;
  member: MemberRow | null;
  onChanged: () => void;
}) {
  const { lang } = useLang();
  const t = adminContent[lang];
  const p = portalContent[lang].portal;
  const [status, setStatus] = useState<StatusKey>(app.status as StatusKey);
  const [reviewerNotes, setReviewerNotes] = useState(app.reviewer_notes ?? "");
  const [comment, setComment] = useState("");
  const [groupId, setGroupId] = useState(member?.group_id ?? "");
  const [busy, setBusy] = useState(false);

  // Assignment depends on the persisted status, not the unsaved dropdown value.
  const assignable = app.status === "accepted" || app.status === "paid";
  const eligibleGroups = groups.filter(
    (g) => g.pathway === app.pathway && (g.is_active || g.id === member?.group_id),
  );
  function seatsLeft(group: GroupRow) {
    const taken = memberCounts[group.id] ?? 0;
    return group.capacity - (member?.group_id === group.id ? taken - 1 : taken);
  }

  async function saveStatus() {
    setBusy(true);
    const { error } = await supabase
      .from("applications")
      .update({
        status,
        reviewer_notes: reviewerNotes.trim() ? reviewerNotes.trim() : null,
      })
      .eq("id", app.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.apps.saved);
    onChanged();
  }

  async function addComment() {
    const value = comment.trim();
    if (value.length < 2) return;
    setBusy(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("application_events").insert({
      application_id: app.id,
      actor_id: auth.user?.id ?? null,
      comment: value,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setComment("");
    toast.success(t.apps.commentSaved);
    onChanged();
  }

  async function saveGroup() {
    setBusy(true);
    if (!groupId) {
      if (member) {
        const { error } = await supabase.from("group_members").delete().eq("id", member.id);
        setBusy(false);
        if (error) {
          toast.error(error.message);
          return;
        }
        toast.success(t.apps.assigned);
        onChanged();
        return;
      }
      setBusy(false);
      return;
    }
    const target = eligibleGroups.find((g) => g.id === groupId);
    if (target && seatsLeft(target) <= 0) {
      setBusy(false);
      toast.error(t.apps.groupFull);
      return;
    }
    const { error } = await supabase
      .from("group_members")
      .upsert(
        { group_id: groupId, application_id: app.id, user_id: app.user_id },
        { onConflict: "application_id" },
      );
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.apps.assigned);
    onChanged();
  }

  return (
    <article className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">{app.full_name ?? app.user_id.slice(0, 8)}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {p.pathways[app.pathway as PathwayKey]} · {app.country} · {app.medical_area}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {formatDate(app.created_at)}
            {app.amount
              ? ` · ${t.apps.fee}: ${money(Number(app.amount), app.currency as CurrencyKey)}`
              : ""}
          </p>
        </div>
        <span className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
          {p.statuses[app.status]}
        </span>
      </div>

      {app.notes && (
        <p className="mt-4 rounded-lg border border-border bg-background/40 p-3 text-xs leading-relaxed text-muted-foreground">
          {app.notes}
        </p>
      )}

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">{t.apps.status}</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusKey)}
            className={`${inputClass} mt-1.5`}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {p.statuses[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">{t.apps.group}</label>
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            disabled={!assignable}
            className={`${inputClass} mt-1.5 disabled:opacity-50`}
          >
            <option value="">{t.apps.noGroup}</option>
            {eligibleGroups.map((g) => (
              <option key={g.id} value={g.id} disabled={seatsLeft(g) <= 0}>
                {g.name} · {seatsLeft(g)} {adminContent[lang].groups.seats}
              </option>
            ))}
          </select>
          {!assignable ? (
            <p className="mt-1.5 text-[11px] text-muted-foreground">{t.apps.acceptOnlyGroup}</p>
          ) : eligibleGroups.length === 0 ? (
            <p className="mt-1.5 text-[11px] text-muted-foreground">{t.apps.noGroupForPathway}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs font-semibold text-muted-foreground">
          {t.apps.reviewerNotes}
        </label>
        <textarea
          value={reviewerNotes}
          onChange={(e) => setReviewerNotes(e.target.value)}
          rows={2}
          placeholder={t.apps.reviewerNotesPlaceholder}
          className={`${inputClass} mt-1.5 resize-y`}
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={saveStatus} disabled={busy} className={btnPrimary}>
          {t.apps.saveStatus}
        </button>
        <button onClick={saveGroup} disabled={busy || !assignable} className={btnGhost}>
          {t.apps.assign}
        </button>
        <EmailComposer
          recipients={[{ name: app.full_name ?? "", email: app.email ?? null }]}
          applicationId={app.id}
          groupId={member?.group_id}
          groupName={groups.find((g) => g.id === member?.group_id)?.name}
          startsOn={groups.find((g) => g.id === member?.group_id)?.starts_on ?? undefined}
        />
      </div>

      <div className="mt-6 border-t border-border pt-5">
        <h4 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
          {t.apps.history}
        </h4>
        {events.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">{t.apps.historyEmpty}</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {events.map((e) => (
              <li key={e.id} className="text-xs leading-relaxed text-muted-foreground">
                <span className="text-foreground/80">{formatDateTime(e.created_at)}</span>
                {e.to_status ? (
                  <>
                    {" — "}
                    {e.from_status ? `${p.statuses[e.from_status]} → ` : ""}
                    <span className="text-primary">{p.statuses[e.to_status]}</span>
                  </>
                ) : null}
                {e.comment ? ` — ${e.comment}` : ""}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4">
          <label className="text-xs font-semibold text-muted-foreground">{t.apps.addComment}</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder={t.apps.commentPlaceholder}
            className={`${inputClass} mt-1.5 resize-y`}
          />
          <button onClick={addComment} disabled={busy} className={`${btnGhost} mt-3`}>
            {t.apps.sendComment}
          </button>
        </div>
      </div>
    </article>
  );
}

function NewGroupForm({ onCreated }: { onCreated: () => void }) {
  const { lang } = useLang();
  const t = adminContent[lang].groups;
  const p = portalContent[lang].portal;
  const [name, setName] = useState("");
  const [pathway, setPathway] = useState<PathwayKey>("basic");
  const [startsOn, setStartsOn] = useState("");
  const [capacity, setCapacity] = useState(10);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const parsed = groupSchema.safeParse({
      name,
      pathway,
      starts_on: startsOn,
      capacity: Number(capacity),
      notes,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid data");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("groups").insert({
      name: parsed.data.name,
      pathway: parsed.data.pathway,
      starts_on: parsed.data.starts_on ? parsed.data.starts_on : null,
      capacity: parsed.data.capacity,
      notes: parsed.data.notes ? parsed.data.notes : null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.created);
    setName("");
    setNotes("");
    onCreated();
  }

  return (
    <form onSubmit={create} className={`${cardClass} h-fit`}>
      <h3 className="text-base font-semibold">{t.heading}</h3>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t.lead}</p>

      <label className="mt-5 block text-xs font-semibold text-muted-foreground">{t.name}</label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t.namePlaceholder}
        className={`${inputClass} mt-1.5`}
      />

      <label className="mt-4 block text-xs font-semibold text-muted-foreground">{t.pathway}</label>
      <select
        value={pathway}
        onChange={(e) => setPathway(e.target.value as PathwayKey)}
        className={`${inputClass} mt-1.5`}
      >
        {(["basic", "intermediate", "advanced"] as const).map((key) => (
          <option key={key} value={key}>
            {p.pathways[key]}
          </option>
        ))}
      </select>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground">{t.startsOn}</label>
          <input
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            className={`${inputClass} mt-1.5`}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground">{t.capacity}</label>
          <input
            type="number"
            min={1}
            max={200}
            value={capacity}
            onChange={(e) => setCapacity(Number(e.target.value))}
            className={`${inputClass} mt-1.5`}
          />
        </div>
      </div>

      <label className="mt-4 block text-xs font-semibold text-muted-foreground">{t.notes}</label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder={t.notesPlaceholder}
        className={`${inputClass} mt-1.5 resize-y`}
      />

      <button type="submit" disabled={busy} className={`${btnPrimary} mt-5`}>
        {t.create}
      </button>
    </form>
  );
}

function GroupCard({
  group,
  members,
  apps,
  onChanged,
}: {
  group: GroupRow;
  members: MemberRow[];
  apps: AppRow[];
  onChanged: () => void;
}) {
  const { lang } = useLang();
  const t = adminContent[lang].groups;
  const ta = adminContent[lang].apps;
  const p = portalContent[lang].portal;
  const [busy, setBusy] = useState(false);

  async function toggleActive() {
    setBusy(true);
    const { error } = await supabase
      .from("groups")
      .update({ is_active: !group.is_active })
      .eq("id", group.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    onChanged();
  }

  async function remove(memberId: string) {
    setBusy(true);
    const { error } = await supabase.from("group_members").delete().eq("id", memberId);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    onChanged();
  }

  return (
    <article className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{group.name}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {p.pathways[group.pathway as PathwayKey]}
            {group.starts_on ? ` · ${formatDate(group.starts_on)}` : ""}
            {` · ${members.length}/${group.capacity} ${t.seats}`}
          </p>
          {group.notes && <p className="mt-2 text-xs text-muted-foreground">{group.notes}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold tracking-widest uppercase text-muted-foreground">
            {group.is_active ? t.active : t.archived}
          </span>
          <button onClick={toggleActive} disabled={busy} className={btnGhost}>
            {group.is_active ? t.archive : t.activate}
          </button>
        </div>
      </div>

      <EmailComposer
        recipients={members.map((m) => {
          const app = apps.find((a) => a.id === m.application_id);
          return { name: app?.full_name ?? "", email: app?.email ?? null };
        })}
        groupId={group.id}
        groupName={group.name}
        startsOn={group.starts_on ?? undefined}
      />

      <h4 className="mt-5 text-xs font-semibold tracking-widest uppercase text-muted-foreground">
        {t.members}
      </h4>
      {members.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">—</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {members.map((m) => {
            const app = apps.find((a) => a.id === m.application_id);
            return (
              <li key={m.id} className="flex items-center justify-between gap-3 text-xs">
                <span>
                  {app?.full_name ?? m.user_id.slice(0, 8)}
                  {app ? ` · ${p.statuses[app.status]}` : ""}
                </span>
                <button onClick={() => remove(m.id)} disabled={busy} className={btnGhost}>
                  {ta.unassign}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}
